import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GatewayFooter from '../components/GatewayFooter';
import DarkIsland from '../theme/DarkIsland';

// Entry ("превход") — a minimal, unified access gateway to the closed institutional risk system,
// rendered in the Analysis design system (no boxed card): the same top-left logo as the terminal, the
// 64px command grid over #050505, the cmd-* palette, Barlow + JetBrains Mono. Just the mark, the access
// action, and the brand footer — nothing more.

const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const TEXT = 'var(--c-text)';
const DIM = 'var(--c-text-dim)';
const STEEL = 'var(--c-active)'; // cmd-active — the .page-kicker accent
const GOLD = 'var(--c-gold)'; // primary action, as on the Analysis CTAs
const BORDER = 'rgb(var(--c-border-rgb) / 0.9)';
// Full-bleed brand background (black monolith on a dark floor, gold seam) with dark scrims so the
// left-aligned content, header and footer stay legible over it. #050505 is the base behind the image.
const BG_IMG = `${process.env.PUBLIC_URL}/entry-bg.jpg`;
const PAGE_BG =
  'linear-gradient(180deg, rgb(var(--c-bg-rgb) / 0.6) 0%, rgb(var(--c-bg-rgb) / 0) 15%),' + // top scrim (header)
  'linear-gradient(90deg, rgb(var(--c-bg-rgb) / 0.92) 0%, rgb(var(--c-bg-rgb) / 0.5) 42%, rgb(var(--c-bg-rgb) / 0.05) 72%),' + // left scrim (content)
  'linear-gradient(0deg, rgb(var(--c-bg-rgb) / 0.72) 0%, rgb(var(--c-bg-rgb) / 0) 18%),' + // bottom scrim (footer)
  `url("${BG_IMG}") center / cover no-repeat,` +
  'var(--c-bg)';

function useUtcClock(): string {
  const [t, setT] = useState('');
  useEffect(() => {
    const fmt = () => {
      const d = new Date();
      const p = (n: number) => String(n).padStart(2, '0');
      setT(`${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`);
    };
    fmt();
    const id = setInterval(fmt, 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

export default function Landing() {
  const navigate = useNavigate();
  const utc = useUtcClock();

  return (
    <div className="saa-scene" style={{ minHeight: '100vh', background: PAGE_BG, color: TEXT, fontFamily: MONO, display: 'flex', flexDirection: 'column' }}>
      {/* Header — same brand logo + size as the Analysis terminal */}
      <header className="flex items-center justify-between px-4 md:px-10 lg:px-20 py-4" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <img
          src={`${process.env.PUBLIC_URL}/saa-logo.svg`}
          alt="Scientific Analytics Alliance"
          className="h-10 md:h-11"
          style={{ minWidth: 120 }}
        />
        <span style={{ textAlign: 'right', fontSize: 9.5, letterSpacing: '0.16em', color: DIM, lineHeight: 1.8, fontVariantNumeric: 'tabular-nums' }}>
          SAA · SECURE ACCESS<br />{utc || '—'}
        </span>
      </header>

      {/* Main — mark + access action, unified on the page (no boxed card) */}
      <main className="flex-1 flex items-center px-4 md:px-10 lg:px-20 py-12">
        <div style={{ width: '100%', maxWidth: 1120 }}>
          <div style={{ color: STEEL, fontSize: 13, letterSpacing: '0.3em', textTransform: 'uppercase' }}>Institutional Digital-Asset Intelligence</div>

          <div style={{ marginTop: 'clamp(28px, 4vw, 48px)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0 }}>
            <button
              onClick={() => navigate('/app')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 12, background: 'transparent', color: GOLD, border: 'none', padding: '4px 0', fontFamily: MONO, fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer' }}
            >
              Access terminal <span aria-hidden>→</span>
            </button>
            <span style={{ width: 1, height: 22, background: BORDER, margin: '0 24px' }} />
            <button onClick={() => navigate('/login')} style={{ background: 'transparent', color: DIM, border: 'none', padding: '4px 0', fontFamily: MONO, fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer' }}>
              Sign in
            </button>
            <span style={{ width: 1, height: 22, background: BORDER, margin: '0 24px' }} />
            <button onClick={() => navigate('/pricing')} style={{ background: 'transparent', color: DIM, border: 'none', padding: '4px 0', fontFamily: MONO, fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: 'pointer' }}>
              Plans
            </button>
          </div>
        </div>
      </main>

      {/* Footer — one cohesive mono rail: same family, tracking and size across both lines */}
      <DarkIsland><GatewayFooter /></DarkIsland>
    </div>
  );
}
